'use client';

import {
    Alert,
    Box,
    Breadcrumbs,
    Button,
    Checkbox,
    Container,
    FormControlLabel,
    FormGroup,
    LinearProgress,
    Skeleton,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { CardGridSkeleton } from '@odys/design-system/components/CardGridSkeleton';
import { EmptyState } from '@odys/design-system/components/EmptyState';
import { FilterResetButton } from '@odys/design-system/components/FilterResetButton';
import { FilterSectionHeading } from '@odys/design-system/components/FilterSectionHeading';
import { ImageCarousel } from '@odys/design-system/components/ImageCarousel';
import { MobileFiltersDrawer } from '@odys/design-system/components/MobileFiltersDrawer';
import { SearchField } from '@odys/design-system/components/SearchField';
import { ViewToggle } from '@odys/design-system/components/ViewToggle';
import { radius } from '@odys/design-system/tokens/radius';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useLocale, useTranslations } from 'next-intl';
import { useEffect, useMemo, useRef, useState } from 'react';

import type { DmcCategoryCountsDto, DmcPageDto } from '@/app/api/lib/dmcs/dmc.types';
import type { Role } from '@/app/api/lib/permissions/matrix';
import type {
    ListProductsResponse,
    ProductCardDto,
    ProductCategory,
    ProductFacets,
} from '@/app/api/lib/products/product.types';
import { PRODUCT_CATEGORIES } from '@/app/api/lib/products/product.vocabulary';
import { BackToCatalogueLink } from '@/components/common/BackToCatalogueLink';
import { CatalogCard } from '@odys/design-system/components/CatalogCard';
import { CatalogRow } from '@/components/common/catalog/CatalogRow';
import { RegionChips } from '@/components/common/catalog/RegionChips';
import { FilterCheckboxGroup } from '@/components/common/filters/FilterCheckboxGroup';
import { PaxRangeFilter } from '@/components/common/filters/PaxRangeFilter';
import { PriceRangeFilter } from '@/components/common/filters/PriceRangeFilter';
import { IncompleteBadge } from '@/components/common/IncompleteBadge';
import { UrlPagination } from '@/components/common/pagination/UrlPagination';
import { usePageQueryParam } from '@/components/common/pagination/usePageQueryParam';
import { VisitorOverlay } from '@/components/common/VisitorOverlay';
import { NAVBAR_OFFSET } from '@/components/layout/navbar/navbar-offset';
import { CategorySwitcher } from '@/components/products/CategorySwitcher';
import { productDetailHref } from '@/configurations/products/productDetailHref';
import {
    countActiveFilters,
    emptyFilters,
    type ProductArrayFilterKey,
    type ProductFiltersState,
} from '@/components/products/productFilters';
import { ProductPriceLabel } from '@/components/products/ProductPriceLabel';
import { ProductListViewTracker } from '@/components/tracking/ProductListViewTracker';
import { useApiFetch } from '@/configurations/api/useApiFetch';
import { Link } from '@/configurations/i18n/navigation';
import { type Currency, useCurrency } from '@/configurations/providers/CurrencyProvider';

// Multiple de 6 (PPCM des 2 et 3 colonnes de la grille sm/md) pour que la
// dernière ligne d'une page pleine soit toujours complète — sinon 20 % 3 = 2.
const PRODUCTS_PAGE_SIZE = 24;

type DmcProductsPageProps = {
    slug: string;
    dmc: DmcPageDto;
    initialCategoryParam: string | null;
    viewerRole: Role;
};

type CategoryKey = ProductCategory;
// Source unique = PRODUCT_CATEGORIES (évite tout drift quand une catégorie est
// ajoutée, cf. internationalFlight #2118).
const CATEGORY_KEYS: readonly CategoryKey[] = PRODUCT_CATEGORIES;

/** Clés de facet à valeurs (string[]) — exclut l'histogramme de prix. */
type ArrayFacetKey = Exclude<keyof ProductFacets, 'priceHistogram'>;

/**
 * Un emplacement de filtre dans la sidebar. L'ensemble ET l'ordre des filtres
 * d'une catégorie sont décrits ici, à un seul endroit (cf. change
 * `catalog-product-filter-set-order`) — plus de rendu conditionnel dispersé.
 *
 * - `checkbox` : groupe de cases. Valeurs soit lockées (`wiredValues`, chaînes
 *   envoyées telles quelles au backend), soit data-derived (`facetKey`, valeurs
 *   issues des facets backend). `valueLabels` relabellise l'affichage d'une
 *   valeur sans changer la valeur stockée (ex. "Collectif" → "Petit groupe").
 * - `pricing` : fourchette de prix + histogramme (masquée au Visiteur faute
 *   d'histogramme délivré).
 * - `swimmingPool` : toggle booléen « Avec piscine » (Hôtels).
 * - `pax` : single-select « Nombre de pax » (Transports).
 */
type CheckboxSlot = {
    kind: 'checkbox';
    paramKey: ProductArrayFilterKey;
    labelKey: string;
    wiredValues?: readonly string[];
    facetKey?: ArrayFacetKey;
    valueLabels?: Readonly<Record<string, string>>;
};
type FilterSlot =
    | CheckboxSlot
    | { kind: 'pricing' }
    | { kind: 'swimmingPool' }
    | { kind: 'pickUp' }
    | { kind: 'pax' };

const CATEGORY_FILTERS: Record<CategoryKey, readonly FilterSlot[]> = {
    hotels: [
        { kind: 'checkbox', paramKey: 'region', labelKey: 'hotels.region', facetKey: 'region' },
        {
            kind: 'checkbox',
            paramKey: 'destination',
            labelKey: 'hotels.ville',
            facetKey: 'destination',
        },
        { kind: 'pricing' },
        {
            kind: 'checkbox',
            paramKey: 'customerType',
            labelKey: 'hotels.categorie',
            facetKey: 'customerType',
        },
        { kind: 'swimmingPool' },
        { kind: 'checkbox', paramKey: 'labels', labelKey: 'hotels.type', facetKey: 'labels' },
        {
            kind: 'checkbox',
            paramKey: 'breakfastType',
            labelKey: 'hotels.petitDejeuner',
            facetKey: 'breakfastType',
        },
    ],
    activities: [
        {
            kind: 'checkbox',
            paramKey: 'destination',
            labelKey: 'activities.city',
            facetKey: 'destination',
        },
        { kind: 'pricing' },
        {
            kind: 'checkbox',
            paramKey: 'language',
            labelKey: 'activities.language',
            facetKey: 'language',
        },
        {
            kind: 'checkbox',
            paramKey: 'type',
            labelKey: 'activities.type',
            facetKey: 'activityTags',
        },
        { kind: 'pickUp' },
        {
            kind: 'checkbox',
            paramKey: 'shared',
            labelKey: 'activities.accompagnement',
            wiredValues: ['Collectif', 'Privé'],
            valueLabels: { Collectif: 'activities.petitGroupe' },
        },
        { kind: 'checkbox', paramKey: 'region', labelKey: 'activities.region', facetKey: 'region' },
    ],
    transports: [
        {
            kind: 'checkbox',
            paramKey: 'departureCity',
            labelKey: 'transports.departureCity',
            facetKey: 'departureCity',
        },
        {
            kind: 'checkbox',
            paramKey: 'arrivalCity',
            labelKey: 'transports.arrivalCity',
            facetKey: 'arrivalCity',
        },
        { kind: 'pricing' },
        { kind: 'pax' },
    ],
    // Vol international = même jeu que Transport (spec #1851).
    internationalFlight: [
        {
            kind: 'checkbox',
            paramKey: 'departureCity',
            labelKey: 'transports.departureCity',
            facetKey: 'departureCity',
        },
        {
            kind: 'checkbox',
            paramKey: 'arrivalCity',
            labelKey: 'transports.arrivalCity',
            facetKey: 'arrivalCity',
        },
        { kind: 'pricing' },
        { kind: 'pax' },
    ],
    // Jeux de filtres alignés sur la spec PO (issue #1851).
    rentalCars: [
        { kind: 'pricing' },
        {
            kind: 'checkbox',
            paramKey: 'vehicleType',
            labelKey: 'rentalCars.vehicleType',
            facetKey: 'vehicleType',
        },
        {
            kind: 'checkbox',
            paramKey: 'transmission',
            labelKey: 'rentalCars.transmission',
            facetKey: 'transmission',
        },
    ],
    cruises: [
        {
            kind: 'checkbox',
            paramKey: 'destination',
            labelKey: 'cruises.city',
            facetKey: 'destination',
        },
        { kind: 'pricing' },
        // Labels type-activité réutilisés depuis `activities.*` (mêmes concepts ;
        // cf. le kind `pickUp` qui hardcode déjà `activities.pickUp`).
        {
            kind: 'checkbox',
            paramKey: 'language',
            labelKey: 'activities.language',
            facetKey: 'language',
        },
        { kind: 'checkbox', paramKey: 'region', labelKey: 'activities.region', facetKey: 'region' },
        {
            kind: 'checkbox',
            paramKey: 'type',
            labelKey: 'activities.type',
            facetKey: 'activityTags',
        },
        { kind: 'pickUp' },
        {
            kind: 'checkbox',
            paramKey: 'shared',
            labelKey: 'activities.accompagnement',
            wiredValues: ['Collectif', 'Privé'],
            valueLabels: { Collectif: 'activities.petitGroupe' },
        },
    ],
    restaurants: [
        {
            kind: 'checkbox',
            paramKey: 'destination',
            labelKey: 'restaurants.city',
            facetKey: 'destination',
        },
        { kind: 'pricing' },
    ],
    general: [{ kind: 'pricing' }],
};

function isCategoryKey(value: unknown): value is CategoryKey {
    return typeof value === 'string' && (CATEGORY_KEYS as readonly string[]).includes(value);
}

function firstNonEmptyCategory(counts: DmcCategoryCountsDto): CategoryKey | null {
    return CATEGORY_KEYS.find((key) => counts[key] > 0) ?? null;
}

function useDebouncedValue<T>(value: T, delayMs: number): T {
    const [debounced, setDebounced] = useState(value);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(() => {
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => setDebounced(value), delayMs);
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, [value, delayMs]);
    return debounced;
}

export function DmcProductsPage({
    slug,
    dmc,
    initialCategoryParam,
    viewerRole,
}: DmcProductsPageProps) {
    // La fiche DMC est fetchée côté serveur (RSC) et passée en prop. Seule la
    // liste produits reste client (filtres/pagination/devise interactifs).
    return (
        <DmcProductsPageContent
            slug={slug}
            dmc={dmc}
            initialCategoryParam={initialCategoryParam}
            viewerRole={viewerRole}
        />
    );
}

function DmcProductsPageContent({
    slug,
    dmc,
    initialCategoryParam,
    viewerRole,
}: {
    slug: string;
    dmc: DmcPageDto;
    initialCategoryParam: string | null;
    viewerRole: Role;
}) {
    const t = useTranslations('dmcProductsPage');
    const locale = useLocale();
    const apiFetch = useApiFetch();
    const fallbackCategory = useMemo(
        () => firstNonEmptyCategory(dmc.categoryCounts),
        [dmc.categoryCounts],
    );

    const initialActiveCategory = useMemo<CategoryKey | null>(() => {
        const requested =
            initialCategoryParam && isCategoryKey(initialCategoryParam)
                ? initialCategoryParam
                : null;
        const requestedHasContent = requested !== null && dmc.categoryCounts[requested] > 0;
        return requestedHasContent ? requested : fallbackCategory;
        // initialCategoryParam est lu une seule fois au mount — switches ultérieurs
        // passent par setActiveCategory + history.replaceState, sans navigation
        // Next.js (cf. https://nextjs.org/docs/app/api-reference/functions/use-router#using-the-windowhistory-api).
    }, [initialCategoryParam, fallbackCategory, dmc.categoryCounts]);

    const theme = useTheme();
    // En dessous de `md` la grille passe en une colonne et la sidebar s'empilerait
    // au-dessus des résultats : on bascule alors les filtres dans un bottom sheet.
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const { currency } = useCurrency();
    const [activeCategory, setActiveCategory] = useState<CategoryKey | null>(initialActiveCategory);
    const [view, setView] = useState<'cards' | 'list'>('cards');
    const [search, setSearch] = useState('');
    const [filters, setFilters] = useState<ProductFiltersState>(emptyFilters);
    // Filtre owner-DMC "Données incomplètes" — réservé au DMC propriétaire de la
    // fiche (`dmc.isOwnPage`), pas à tout viewer DMC : un DMC consultant une AUTRE
    // DMC ne le voit pas. Ignoré côté serveur pour tout non-propriétaire de toute façon.
    const [incompleteOnly, setIncompleteOnly] = useState(false);

    const switchCategory = (next: CategoryKey) => {
        if (next === activeCategory) return;
        setActiveCategory(next);
        setSearch('');
        setFilters(emptyFilters);
        setIncompleteOnly(false);
        if (typeof window !== 'undefined') {
            window.scrollTo({ top: 0, behavior: 'auto' });
            const url = `/dmcs/${slug}/products?category=${next}`;
            window.history.replaceState(null, '', url);
        }
    };

    const resetFilters = () => {
        setFilters(emptyFilters);
        setSearch('');
        setIncompleteOnly(false);
    };

    // Reset price range when currency changes (amounts are not comparable across currencies)
    const prevCurrencyRef = useRef(currency);
    useEffect(() => {
        if (prevCurrencyRef.current !== currency) {
            prevCurrencyRef.current = currency;
            setFilters((prev) => ({ ...prev, priceMin: null, priceMax: null }));
        }
    }, [currency]);

    const debouncedSearch = useDebouncedValue(search.trim(), 250);
    const { page, resetOnFiltersChange } = usePageQueryParam();

    // Reset paginator à la page 1 dès qu'un filtre / search / catégorie change
    // (cf. spec `pagination` D4). Le useEffect garde la rule centralisée — chaque
    // setFilter / setActiveCategory n'a pas à se préoccuper de la pagination.
    // On saute le PREMIER rendu : sinon une adresse profonde (`?page=N` partagée
    // ou rafraîchie) verrait sa page strippée au montage et retomberait page 1
    // (cf. spec `pagination` : refresh/share restaure la même page).
    const filtersKey = useMemo(() => JSON.stringify(filters), [filters]);
    const skipFirstReset = useRef(true);
    useEffect(() => {
        if (skipFirstReset.current) {
            skipFirstReset.current = false;
            return;
        }
        resetOnFiltersChange();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- déps = les triggers (catégorie/search/filtres) ; resetOnFiltersChange est exclu volontairement pour ne pas relancer le reset sur un simple changement d'identité du callback.
    }, [activeCategory, debouncedSearch, filtersKey, incompleteOnly]);

    const productsQuery = useQuery<ListProductsResponse, Error>({
        queryKey: [
            'dmc-products',
            locale,
            slug,
            activeCategory,
            debouncedSearch,
            filters,
            currency,
            incompleteOnly,
            page,
            currency,
        ],
        queryFn: async () => {
            const params = new URLSearchParams({ category: activeCategory ?? '' });
            if (debouncedSearch.length > 0) params.set('search', debouncedSearch);
            for (const [key, value] of Object.entries(filters)) {
                if (Array.isArray(value)) for (const v of value) params.append(key, v);
            }
            if (filters.swimmingPool) params.set('swimmingPool', 'true');
            if (filters.pickUp) params.set('pickUp', 'true');
            params.set('currency', currency);
            params.set('priceCurrency', currency);
            if (filters.priceMin !== null) params.set('priceMin', String(filters.priceMin));
            if (filters.priceMax !== null) params.set('priceMax', String(filters.priceMax));
            if (incompleteOnly) params.set('incomplete', 'true');
            if (page > 1) params.set('page', String(page));
            params.set('pageSize', String(PRODUCTS_PAGE_SIZE));
            const res = await apiFetch(`/api/dmcs/${slug}/products?${params.toString()}`);
            if (!res.ok) throw new Error(`Products fetch failed (${res.status})`);
            return ((await res.json()) as { data: ListProductsResponse }).data;
        },
        // Inter-page : on garde le rendu précédent (cards + facets stables) le
        // temps du refetch — c'est le "smooth refresh" exigé par la spec D6.
        // Sur changement de catégorie le queryKey diffère totalement → skeleton.
        placeholderData: keepPreviousData,
        enabled: activeCategory !== null,
    });

    const facets: ProductFacets | undefined = productsQuery.data?.facets;
    const items = productsQuery.data?.items ?? [];
    const meta = productsQuery.data?.meta;

    if (activeCategory === null) {
        return <EmptyDmcShell dmc={dmc} viewerRole={viewerRole} />;
    }

    const categoryLabel = t(`categoryLabels.${activeCategory}`);
    const productsCount = meta?.total;
    const activeFilterCount = countActiveFilters(filters, incompleteOnly);

    // Sidebar de filtres rendue soit inline (desktop), soit dans le bottom sheet
    // mobile — un seul montage à la fois (isMobile XOR) pour éviter de dupliquer
    // les `data-testid` et l'état des sous-filtres.
    const filterSidebar = (
        <FilterSidebar
            category={activeCategory}
            filters={filters}
            facets={facets}
            currency={currency}
            onFilterToggle={(paramKey, value) => {
                setFilters((prev) => {
                    const current = prev[paramKey] as string[];
                    const next = current.includes(value)
                        ? current.filter((v) => v !== value)
                        : [...current, value];
                    return { ...prev, [paramKey]: next };
                });
            }}
            onFilterSet={(paramKey, values) => {
                setFilters((prev) => ({ ...prev, [paramKey]: values }));
            }}
            onPriceChange={(range) =>
                setFilters((prev) => ({
                    ...prev,
                    priceMin: range ? range[0] : null,
                    priceMax: range ? range[1] : null,
                }))
            }
            onReset={resetFilters}
            swimmingPool={filters.swimmingPool}
            pickUp={filters.pickUp}
            onPickUpToggle={() => setFilters((prev) => ({ ...prev, pickUp: !prev.pickUp }))}
            onSwimmingPoolToggle={() =>
                setFilters((prev) => ({ ...prev, swimmingPool: !prev.swimmingPool }))
            }
            showIncompleteFilter={dmc.isOwnPage}
            incompleteOnly={incompleteOnly}
            onIncompleteToggle={() => setIncompleteOnly((v) => !v)}
        />
    );

    return (
        <Container
            maxWidth="lg"
            sx={{
                py: 4,
                pb: viewerRole === 'visitor' ? { xs: 16, sm: 12 } : 4,
            }}
        >
            <Stack spacing={3}>
                {/* Monté ici (et pas dans la page serveur) pour être indexé sur la
                    catégorie ACTIVE : le switch de catégorie ne provoque pas de
                    navigation, donc seul ce state reflète la vue réellement affichée.
                    Ne rend rien — le `gap` du Stack n'en tient pas compte. */}
                <ProductListViewTracker dmcId={dmc.identity.id} category={activeCategory} />
                <Breadcrumbs aria-label="breadcrumb" data-testid="dmc-products-breadcrumb">
                    <Link href="/" style={{ color: 'inherit', textDecoration: 'none' }}>
                        {t('breadcrumbHome')}
                    </Link>
                    <Link
                        href={`/dmcs/${dmc.identity.slug}`}
                        style={{ color: 'inherit', textDecoration: 'none' }}
                    >
                        {dmc.identity.name}
                    </Link>
                    <Typography
                        sx={{
                            color: 'text.primary',
                        }}
                    >
                        {categoryLabel}
                    </Typography>
                </Breadcrumbs>

                <Box data-testid="dmc-products-header">
                    <Box sx={{ mb: 1.5 }}>
                        <BackToCatalogueLink
                            dmcSlug={dmc.identity.slug}
                            label={t('backLink')}
                            data-testid="dmc-products-back-link"
                        />
                    </Box>
                    <Stack
                        direction="row"
                        spacing={2}
                        sx={{
                            alignItems: 'center',
                            flexWrap: 'wrap',
                        }}
                    >
                        <Typography variant="h3" component="h1">
                            {t('headerPattern', {
                                category: categoryLabel,
                                dmcName: dmc.identity.name,
                            })}
                        </Typography>
                        {productsCount !== undefined ? (
                            <Typography
                                variant="body2"
                                data-testid="dmc-products-count"
                                sx={{
                                    color: 'text.secondary',
                                }}
                            >
                                {t('productsCount', { count: productsCount })}
                            </Typography>
                        ) : (
                            <Skeleton variant="text" width={80} />
                        )}
                    </Stack>
                </Box>

                <CategorySwitcher
                    categoryCounts={dmc.categoryCounts}
                    dmcSlug={dmc.identity.slug}
                    hasItineraries={dmc.counts.itineraries > 0}
                    active={{
                        kind: 'category',
                        category: activeCategory,
                        onSwitch: switchCategory,
                    }}
                />

                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: '260px minmax(0, 1fr)' },
                        gap: 3,
                    }}
                >
                    {!isMobile && filterSidebar}

                    <Stack spacing={2}>
                        {isMobile && (
                            <Box>
                                <MobileFiltersDrawer
                                    activeCount={activeFilterCount}
                                    label={t('filters.openButton')}
                                    closeLabel={t('filters.closeDrawer')}
                                    applyLabel={t('filters.applyButton', {
                                        count: productsCount ?? 0,
                                    })}
                                    testIdPrefix="dmc-products-filters"
                                >
                                    {filterSidebar}
                                </MobileFiltersDrawer>
                            </Box>
                        )}
                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={2}
                            sx={{
                                alignItems: { sm: 'center' },
                                justifyContent: 'space-between',
                            }}
                        >
                            <SearchField
                                value={search}
                                onChange={setSearch}
                                placeholder={t('search.placeholder')}
                                fullWidth
                                data-testid="dmc-products-search"
                            />
                            <ViewToggle
                                value={view}
                                onChange={setView}
                                cardsLabel={t('view.cards')}
                                listLabel={t('view.list')}
                                testIdPrefix="dmc-products-view"
                            />
                        </Stack>

                        <ProductsResults
                            slug={slug}
                            category={activeCategory}
                            view={view}
                            viewerRole={viewerRole}
                            isPending={productsQuery.isPending}
                            error={productsQuery.error}
                            items={items}
                            hasActiveSearch={debouncedSearch.length > 0}
                            totalPages={meta?.totalPages ?? 1}
                            isFetching={productsQuery.isFetching}
                        />
                    </Stack>
                </Box>
            </Stack>
        </Container>
    );
}

function FilterSidebar({
    category,
    filters,
    facets,
    onFilterToggle,
    onFilterSet,
    onPriceChange,
    onReset,
    currency,
    swimmingPool,
    onSwimmingPoolToggle,
    pickUp,
    onPickUpToggle,
    showIncompleteFilter,
    incompleteOnly,
    onIncompleteToggle,
}: {
    category: CategoryKey;
    filters: ProductFiltersState;
    facets: ProductFacets | undefined;
    onFilterToggle: (paramKey: ProductArrayFilterKey, value: string) => void;
    onFilterSet: (paramKey: ProductArrayFilterKey, values: string[]) => void;
    onPriceChange: (range: [number, number] | null) => void;
    onReset: () => void;
    currency: Currency;
    swimmingPool: boolean;
    onSwimmingPoolToggle: () => void;
    pickUp: boolean;
    onPickUpToggle: () => void;
    showIncompleteFilter: boolean;
    incompleteOnly: boolean;
    onIncompleteToggle: () => void;
}) {
    const t = useTranslations('dmcProductsPage.filters');

    const hasActiveFilters = countActiveFilters(filters, incompleteOnly) > 0;

    const priceValue: [number, number] | null =
        filters.priceMin !== null && filters.priceMax !== null
            ? [filters.priceMin, filters.priceMax]
            : null;

    const groupLabels = {
        searchPlaceholder: t('searchPlaceholder'),
        searchEmpty: t('searchEmpty'),
        seeAll: t('seeAll'),
        seeLess: t('seeLess'),
    };

    // Set + ordre pilotés par CATEGORY_FILTERS : chaque emplacement est rendu
    // selon son `kind`. Un groupe de cases sans valeur (facet vide) ne s'affiche
    // pas ; le Pricing n'apparaît que si un histogramme est délivré (donc jamais
    // au Visiteur).
    const renderCheckbox = (slot: CheckboxSlot) => {
        const values = slot.wiredValues ?? (slot.facetKey ? facets?.[slot.facetKey] : undefined);
        if (!values || values.length === 0) return null;
        return (
            <FilterCheckboxGroup
                key={slot.labelKey}
                title={t(slot.labelKey)}
                options={values.map((value) => ({
                    value,
                    label: slot.valueLabels?.[value] ? t(slot.valueLabels[value]) : value,
                }))}
                selected={filters[slot.paramKey]}
                onToggle={(value) => onFilterToggle(slot.paramKey, value)}
                testIdPrefix={`dmc-products-filter-${slot.paramKey}`}
                labels={groupLabels}
            />
        );
    };

    const renderSlot = (slot: FilterSlot) => {
        switch (slot.kind) {
            case 'checkbox':
                return renderCheckbox(slot);
            case 'pricing':
                return facets?.priceHistogram ? (
                    <PriceRangeFilter
                        key="pricing"
                        value={priceValue}
                        onChange={onPriceChange}
                        histogram={facets.priceHistogram}
                        currency={currency}
                        label={t('hotels.pricing')}
                        resetLabel={t('price.reset')}
                    />
                ) : null;
            case 'swimmingPool':
                return (
                    <BooleanToggleFilter
                        key="swimmingPool"
                        testId="swimmingPool"
                        label={t('hotels.piscine')}
                        optionLabel={t('hotels.swimmingPoolOption')}
                        checked={swimmingPool}
                        onToggle={onSwimmingPoolToggle}
                    />
                );
            case 'pickUp':
                return (
                    <BooleanToggleFilter
                        key="pickUp"
                        testId="pickUp"
                        label={t('activities.pickUp')}
                        optionLabel={t('activities.pickUpOption')}
                        checked={pickUp}
                        onToggle={onPickUpToggle}
                    />
                );
            case 'pax':
                return (
                    <PaxRangeFilter
                        key="pax"
                        label={t('transports.pax')}
                        value={
                            filters.pax.length === 2
                                ? [
                                      Number.parseInt(filters.pax[0], 10),
                                      Number.parseInt(filters.pax[1], 10),
                                  ]
                                : null
                        }
                        onChange={(range) =>
                            onFilterSet(
                                'pax',
                                range === null ? [] : [String(range[0]), String(range[1])],
                            )
                        }
                        resetLabel={t('reset')}
                    />
                );
        }
    };

    return (
        <Stack
            spacing={2}
            data-testid="dmc-products-filter-sidebar"
            sx={{
                alignSelf: 'start',
                position: { md: 'sticky' },
                top: { md: `${NAVBAR_OFFSET}px` },
                maxHeight: { md: `calc(100vh - ${NAVBAR_OFFSET}px - 16px)` },
                overflowY: { md: 'auto' },
                // Keep the sticky panel clear of its own scrollbar gutter.
                pr: { md: 1 },
                '&::-webkit-scrollbar': { width: 6 },
                '&::-webkit-scrollbar-thumb': {
                    bgcolor: 'divider',
                    borderRadius: radius.xl,
                },
            }}
        >
            <FilterResetButton
                label={t('resetButton')}
                onClick={onReset}
                disabled={!hasActiveFilters}
                data-testid="dmc-products-filters-reset"
            />

            {showIncompleteFilter && (
                <Box data-testid="dmc-products-filter-incomplete">
                    <FilterSectionHeading>{t('incomplete.label')}</FilterSectionHeading>
                    <FormGroup sx={{ pl: 0.5 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    size="small"
                                    checked={incompleteOnly}
                                    onChange={onIncompleteToggle}
                                    data-testid="dmc-products-filter-incomplete-checkbox"
                                />
                            }
                            label={
                                <Typography variant="body2">{t('incomplete.option')}</Typography>
                            }
                        />
                    </FormGroup>
                </Box>
            )}

            {CATEGORY_FILTERS[category].map(renderSlot)}
        </Stack>
    );
}

/**
 * Filtre booléen mono-option (ex. Hôtels « Piscine », Activités « Pick-up ») :
 * une seule case qui, cochée, restreint aux Produits ayant la caractéristique.
 */
function BooleanToggleFilter({
    testId,
    label,
    optionLabel,
    checked,
    onToggle,
}: {
    testId: string;
    label: string;
    optionLabel: string;
    checked: boolean;
    onToggle: () => void;
}) {
    return (
        <Box data-testid={`dmc-products-filter-${testId}`}>
            <FilterSectionHeading>{label}</FilterSectionHeading>
            <FormGroup sx={{ pl: 0.5 }}>
                <FormControlLabel
                    control={
                        <Checkbox
                            size="small"
                            checked={checked}
                            onChange={onToggle}
                            data-testid={`dmc-products-filter-${testId}-checkbox`}
                        />
                    }
                    label={<Typography variant="body2">{optionLabel}</Typography>}
                />
            </FormGroup>
        </Box>
    );
}

function EditInOdysButton({ href, productId }: { href: string; productId: string }) {
    const t = useTranslations('dmcProductsPage.dmcCardActions');
    return (
        <Button
            component="a"
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            variant="outlined"
            onClick={(e) => e.stopPropagation()}
            data-testid={`dmc-products-edit-in-odys-${productId}`}
        >
            {t('editInOdys')}
        </Button>
    );
}

function ProductsResults({
    slug,
    category,
    view,
    viewerRole,
    isPending,
    error,
    items,
    hasActiveSearch,
    totalPages,
    isFetching,
}: {
    slug: string;
    category: CategoryKey;
    view: 'cards' | 'list';
    viewerRole: Role;
    isPending: boolean;
    error: Error | null;
    items: ProductCardDto[];
    hasActiveSearch: boolean;
    totalPages: number;
    isFetching: boolean;
}) {
    if (isPending) {
        return <CardGridSkeleton view={view} />;
    }

    if (error) {
        return (
            <Alert severity="error" data-testid="dmc-products-results-error">
                {error.message}
            </Alert>
        );
    }

    if (items.length === 0) {
        return <NoResults afterFilter={hasActiveSearch} />;
    }

    return (
        <Stack spacing={2}>
            <Box sx={{ position: 'relative' }}>
                {isFetching && (
                    <LinearProgress
                        data-testid="dmc-products-results-loading"
                        sx={{
                            position: 'absolute',
                            top: -8,
                            left: 0,
                            right: 0,
                            borderRadius: radius.sm,
                        }}
                    />
                )}
                <Box
                    sx={{
                        opacity: isFetching ? 0.6 : 1,
                        transition: 'opacity .15s ease',
                        pointerEvents: isFetching ? 'none' : 'auto',
                    }}
                >
                    {view === 'list' ? (
                        <ProductRows
                            items={items}
                            viewerRole={viewerRole}
                            slug={slug}
                            category={category}
                        />
                    ) : (
                        <ProductCards
                            items={items}
                            viewerRole={viewerRole}
                            slug={slug}
                            category={category}
                        />
                    )}
                </Box>
            </Box>
            <UrlPagination
                count={totalPages}
                loading={isFetching}
                data-testid="dmc-products-pagination"
            />
        </Stack>
    );
}

function ProductCards({
    items,
    viewerRole,
    slug,
    category,
}: {
    items: ProductCardDto[];
    viewerRole: Role;
    slug: string;
    category: CategoryKey;
}) {
    return (
        <Box
            data-testid="dmc-products-results-cards"
            sx={{
                display: 'grid',
                gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, minmax(0, 1fr))',
                    md: 'repeat(3, minmax(0, 1fr))',
                },
                gap: 2,
            }}
        >
            {items.map((p) => (
                <ProductCardWrapper
                    key={p.id}
                    product={p}
                    viewerRole={viewerRole}
                    slug={slug}
                    category={category}
                />
            ))}
        </Box>
    );
}

function ProductCardWrapper({
    product,
    viewerRole,
    slug,
    category,
}: {
    product: ProductCardDto;
    viewerRole: Role;
    slug: string;
    category: CategoryKey;
}) {
    const tVisitor = useTranslations('dmcProductsPage.visitorOverlay');

    if (viewerRole === 'visitor') {
        return (
            <VisitorOverlay
                message={tVisitor('message')}
                ctaLabel={tVisitor('cta')}
                testIdPrefix="dmc-products-visitor-overlay"
            >
                <ProductCardBody product={product} interactive={false} />
            </VisitorOverlay>
        );
    }

    // Agency et DMC cliquent vers la fiche produit.
    return (
        <Link
            href={productDetailHref(slug, category, product.id)}
            style={{ textDecoration: 'none', color: 'inherit' }}
            data-testid={`dmc-products-card-link-${product.id}`}
        >
            <ProductCardBody product={product} interactive />
        </Link>
    );
}

/**
 * Marqueur "Données incomplètes" affiché sur une carte grisée. N'apparaît que
 * pour le DMC propriétaire (les non-propriétaires ne reçoivent jamais d'item
 * incomplet — porte de qualité côté serveur).
 */
/**
 * Ville affichée sur la carte / la ligne. Pour un Transport ayant une ville
 * d'arrivée, compose le trajet « départ → arrivée » ; sinon retombe sur la
 * ville de départ seule (`arrivalCity` est null hors Transports).
 */
function productVille(product: ProductCardDto): string | null {
    if (product.city && product.arrivalCity) {
        return `${product.city} → ${product.arrivalCity}`;
    }
    return product.city;
}

function ProductCardBody({
    product,
    interactive,
}: {
    product: ProductCardDto;
    interactive: boolean;
}) {
    const tImageCarousel = useTranslations('imageCarousel');
    const tCard = useTranslations('dmcProductsPage.card');

    return (
        <CatalogCard
            data-testid={`dmc-products-card-${product.id}`}
            dimmed={product.isIncomplete}
            title={product.name}
            meta={productVille(product)}
            media={
                <Box data-image={product.imageUrls.length > 0 ? 'present' : 'placeholder'}>
                    <ImageCarousel
                        id={product.id}
                        imageUrls={interactive ? product.imageUrls : product.imageUrls.slice(0, 1)}
                        interactive={interactive}
                        prevLabel={tImageCarousel('prev')}
                        nextLabel={tImageCarousel('next')}
                    />
                </Box>
            }
            badge={
                product.isIncomplete ? (
                    <IncompleteBadge
                        label={tCard('incompleteBadge')}
                        data-testid="dmc-products-incomplete-badge"
                    />
                ) : undefined
            }
            chips={
                product.regions.length > 0 ? (
                    <RegionChips
                        regions={product.regions}
                        data-testid="dmc-products-region-chips"
                    />
                ) : undefined
            }
            price={product.priceFrom ? <ProductPriceLabel price={product.priceFrom} /> : undefined}
            action={
                product.editInOdysUrl ? (
                    <EditInOdysButton href={product.editInOdysUrl} productId={product.id} />
                ) : undefined
            }
        />
    );
}

function ProductRows({
    items,
    viewerRole,
    slug,
    category,
}: {
    items: ProductCardDto[];
    viewerRole: Role;
    slug: string;
    category: CategoryKey;
}) {
    return (
        <Stack spacing={1} data-testid="dmc-products-results-list">
            {items.map((p) => (
                <ProductRowWrapper
                    key={p.id}
                    product={p}
                    viewerRole={viewerRole}
                    slug={slug}
                    category={category}
                />
            ))}
        </Stack>
    );
}

function ProductRowWrapper({
    product,
    viewerRole,
    slug,
    category,
}: {
    product: ProductCardDto;
    viewerRole: Role;
    slug: string;
    category: CategoryKey;
}) {
    const tVisitor = useTranslations('dmcProductsPage.visitorOverlay');

    if (viewerRole === 'visitor') {
        return (
            <VisitorOverlay
                message={tVisitor('message')}
                ctaLabel={tVisitor('cta')}
                testIdPrefix="dmc-products-visitor-overlay"
            >
                <ProductRowBody product={product} />
            </VisitorOverlay>
        );
    }

    // Agency et DMC cliquent vers la fiche produit ; seul Visitor est
    // bloqué par l'overlay signup.
    return (
        <Link
            href={productDetailHref(slug, category, product.id)}
            style={{ display: 'block', textDecoration: 'none', color: 'inherit' }}
            data-testid={`dmc-products-row-link-${product.id}`}
        >
            <ProductRowBody product={product} />
        </Link>
    );
}

function ProductRowBody({ product }: { product: ProductCardDto }) {
    const tCard = useTranslations('dmcProductsPage.card');

    return (
        <CatalogRow
            data-testid={`dmc-products-row-${product.id}`}
            dimmed={product.isIncomplete}
            imageUrl={product.imageUrls[0]}
            title={product.name}
            meta={productVille(product)}
            badge={
                product.isIncomplete ? (
                    <IncompleteBadge
                        label={tCard('incompleteBadge')}
                        data-testid="dmc-products-incomplete-badge"
                    />
                ) : undefined
            }
            price={product.priceFrom ? <ProductPriceLabel price={product.priceFrom} /> : undefined}
            action={
                product.editInOdysUrl ? (
                    <EditInOdysButton href={product.editInOdysUrl} productId={product.id} />
                ) : undefined
            }
        />
    );
}

function NoResults({ afterFilter }: { afterFilter: boolean }) {
    const t = useTranslations('dmcProductsPage.emptyState');
    return (
        <EmptyState
            data-testid="dmc-products-results-empty"
            filtered={afterFilter}
            title={afterFilter ? t('noResultAfterFilters') : t('noProductAtAll')}
            body={afterFilter ? t('noResultSuggestion') : undefined}
        />
    );
}

function EmptyDmcShell({ dmc, viewerRole }: { dmc: DmcPageDto; viewerRole: Role }) {
    const t = useTranslations('dmcProductsPage');
    return (
        <Container
            maxWidth="lg"
            sx={{
                py: 4,
                pb: viewerRole === 'visitor' ? { xs: 16, sm: 12 } : 4,
            }}
        >
            <Stack spacing={3}>
                <Breadcrumbs aria-label="breadcrumb">
                    <Link href="/" style={{ color: 'inherit', textDecoration: 'none' }}>
                        {t('breadcrumbHome')}
                    </Link>
                    <Link
                        href={`/dmcs/${dmc.identity.slug}`}
                        style={{ color: 'inherit', textDecoration: 'none' }}
                    >
                        {dmc.identity.name}
                    </Link>
                </Breadcrumbs>
                <Box data-testid="dmc-products-empty-no-product-at-all">
                    <Alert severity="info">{t('emptyState.noProductAtAll')}</Alert>
                </Box>
            </Stack>
        </Container>
    );
}
